import type { ReactNode } from "react";
import { Trans } from "@lingui/react/macro";
import {
  ChevronDown,
  ChevronsLeft,
  ChevronLeft,
  ChevronRight,
  ChevronsRight,
} from "lucide-react";
import { Table, TableCell, TableHead } from "@/components/ui/table";
import { cn } from "@/lib/utils";

/**
 * Presentational shell for the read-only account tables (login history,
 * invoices, activity log). Structure comes from `@/components/ui/table` —
 * this only adds the bordered card and the design-token cell styling.
 *
 * Consumers pair it with `TableHeader` / `TableBody` / `TableRow` straight from
 * `@/components/ui/table`. Real sorting / paging is out of scope for the
 * frontend-only build, so the pagination bar below is static by default.
 */

export function ProfileTable({ children }: { children: ReactNode }) {
  return (
    <div className="overflow-x-auto rounded-xl border border-(--color-border)">
      {/* Row dividers and hover live here so callers can use a bare `TableRow`. */}
      <Table className="border-collapse [&_tbody_tr]:border-(--color-border) [&_tbody_tr]:hover:bg-transparent">
        {children}
      </Table>
    </div>
  );
}

export function Th({
  children,
  sorted,
  className,
}: {
  children: ReactNode;
  sorted?: boolean;
  className?: string;
}) {
  return (
    <TableHead
      className={cn(
        "h-auto bg-(--color-surface-muted) px-5 py-3 text-sm font-medium text-(--color-text-secondary)",
        className,
      )}
    >
      <span className="inline-flex items-center gap-1.5">
        {children}
        {sorted ? <ChevronDown className="size-4" /> : null}
      </span>
    </TableHead>
  );
}

export function Td({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) {
  return (
    <TableCell
      className={cn(
        "px-5 py-4 text-sm text-(--color-text-primary)",
        className,
      )}
    >
      {children}
    </TableCell>
  );
}

export function TablePaginationBar({
  shown,
  total,
  page = 1,
  pageCount = 1,
  rowsPerPage = 10,
  onPageChange,
}: {
  shown: number;
  total: number;
  page?: number;
  pageCount?: number;
  rowsPerPage?: number;
  /** When provided, the pager buttons become interactive. */
  onPageChange?: (page: number) => void;
}) {
  // [first, prev, next, last] targets + per-button disabled state.
  const targets = [1, page - 1, page + 1, pageCount];
  const atStart = page <= 1;
  const atEnd = page >= pageCount;
  const disabled = [atStart, atStart, atEnd, atEnd];

  return (
    <div className="flex flex-col gap-4 px-1 pt-4 text-sm text-(--color-text-secondary) lg:flex-row lg:items-center lg:justify-between">
      <span>
        <Trans>
          {shown} of {total} row(s) shown.
        </Trans>
      </span>
      <div className="flex flex-wrap items-center gap-4">
        <div className="flex items-center gap-2">
          <span>
            <Trans>Rows per page</Trans>
          </span>
          <div className="flex h-9 items-center gap-1 rounded-lg border border-(--color-border) bg-(--color-surface-content) px-2.5 text-(--color-text-primary)">
            {rowsPerPage}
            <ChevronDown className="size-4 text-(--color-text-secondary)" />
          </div>
        </div>
        <span>
          <Trans>
            Page {page} of {pageCount}
          </Trans>
        </span>
        <div className="flex items-center gap-1">
          {[ChevronsLeft, ChevronLeft, ChevronRight, ChevronsRight].map(
            (Icon, i) => (
              <button
                key={i}
                type="button"
                disabled={!onPageChange || disabled[i]}
                onClick={() =>
                  onPageChange?.(Math.min(Math.max(1, targets[i] ?? 1), pageCount))
                }
                className="flex size-9 items-center justify-center rounded-lg border border-(--color-border) bg-(--color-surface-content) text-(--color-text-secondary) disabled:opacity-50"
              >
                <Icon className="size-4" />
              </button>
            ),
          )}
        </div>
      </div>
    </div>
  );
}
